import {
  afterEveryRender,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  input,
  OnDestroy,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import JSONEditor from 'jsoneditor';

/**
 * Round-trip through JSON so Date instances become ISO strings instead of {}.
 * Uses a WeakSet replacer to handle circular references (e.g. scan → productionBatch → scans → scan).
 */
function toJson(value: unknown): unknown {
  if (value === null || value === undefined) return null;
  const seen = new WeakSet();
  return JSON.parse(JSON.stringify(value, (_key, val) => {
    if (typeof val === 'object' && val !== null) {
      if (seen.has(val)) return '[Circular]';
      seen.add(val);
    }
    return val;
  }));
}

/**
 * Generic JSON viewer built on top of the jsoneditor library (Jos de Jong).
 *
 * **Inline mode** (default): renders as a plain block-level editor that fills
 * its host element. Just bind `[data]`.
 *
 * **Floating window mode**: set `[title]` to a non-null string to get a
 * draggable, resizable floating window with a title bar and close button.
 * The window is hidden while `data` is null so you can leave the element in
 * the DOM and toggle visibility by setting `data` to null / a real object.
 * Listen to `(closed)` to clear the data.
 *
 * Features provided by jsoneditor out of the box:
 *  - Tree view with recursive collapse / expand
 *  - Full-text search with match highlighting
 *  - Path breadcrumb navigation
 *  - Sort object keys, copy to clipboard
 *  - Switch between tree / view / code / text display modes via the toolbar
 *
 * Usage (inline):
 *   <app-json-object-preview [data]="anyObject" />
 *
 * Usage (floating):
 *   <app-json-object-preview [data]="model()" [title]="modelTitle()" (closed)="model.set(null)" />
 */
@Component({
  selector: 'app-json-object-preview',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NzButtonModule, NzIconModule],
  template: `
    @if (title() !== null) {
      <!-- Floating window mode — always rendered so the editor is always mounted.
           The outer div is hidden via display:none when data is null. -->
      <div
        class="preview-window"
        [style.display]="data() !== null && data() !== undefined ? 'flex' : 'none'"
        [style.left.px]="x()"
        [style.top.px]="y()"
        role="dialog"
        [attr.aria-label]="title()"
      >
        <div class="title-bar" (mousedown)="onDragStart($event)">
          <span class="title-text">{{ title() }}</span>
          <button
            nz-button
            nzType="text"
            nzSize="small"
            class="close-btn"
            aria-label="Close preview"
            (click)="closed.emit()"
          >
            <span nz-icon nzType="close"></span>
          </button>
        </div>
        <div class="body">
          <div #container class="editor-host"></div>
        </div>
      </div>
    } @else if (data() !== null && data() !== undefined) {
      <!-- Inline mode — the editor fills the host element. -->
      <div #container class="editor-host"></div>
    }
  `,
  styles: [`
    /* ── Inline mode ─────────────────────────────────────────────── */

    :host {
      display: block;
      height: 100%;
    }

    .editor-host {
      height: 100%;
    }

    /* ── Floating window mode ────────────────────────────────────── */

    .preview-window {
      position: fixed;
      z-index: 1050;
      min-width: 340px;
      min-height: 200px;
      width: 60vw;
      height: 80vh;
      background: #1e1e1e;
      border: 1px solid #444;
      border-radius: 6px;
      box-shadow: 0 8px 32px rgba(0,0,0,.55);
      display: flex;
      flex-direction: column;
      user-select: none;
      resize: both;
      overflow: hidden;
    }

    .title-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 6px 10px;
      background: #2d2d2d;
      border-bottom: 1px solid #444;
      border-radius: 6px 6px 0 0;
      cursor: grab;
      flex-shrink: 0;
    }

    .title-bar:active { cursor: grabbing; }

    .title-text {
      font-size: 12px;
      font-weight: 600;
      color: #ccc;
      font-family: monospace;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .close-btn {
      color: #aaa !important;
      flex-shrink: 0;
    }

    .close-btn:hover { color: #fff !important; }

    .body {
      flex: 1;
      overflow: hidden;
      min-height: 0;
      user-select: text;
    }

    /* ── jsoneditor dark theme (both modes) ──────────────────────── */

    :host ::ng-deep .jsoneditor {
      border-color: #444;
    }

    :host ::ng-deep .jsoneditor-menu {
      background-color: #2d2d2d;
      border-bottom-color: #444;
    }

    :host ::ng-deep .jsoneditor-menu button,
    :host ::ng-deep .jsoneditor-menu .jsoneditor-search input {
      color: #ccc;
    }

    :host ::ng-deep .jsoneditor > .jsoneditor-outer {
      background: #1e1e1e;
    }

    :host ::ng-deep .jsoneditor-tree {
      background: #1e1e1e;
      color: #d4d4d4;
    }

    :host ::ng-deep .jsoneditor-field  { color: #9cdcfe; }
    :host ::ng-deep .jsoneditor-value.jsoneditor-string  { color: #ce9178; }
    :host ::ng-deep .jsoneditor-value.jsoneditor-number  { color: #b5cea8; }
    :host ::ng-deep .jsoneditor-value.jsoneditor-boolean { color: #569cd6; }
    :host ::ng-deep .jsoneditor-value.jsoneditor-null    { color: #569cd6; }
  `],
})
export class JsonObjectPreviewComponent implements OnDestroy {
  /** Any value to display. Updating this input re-renders the tree. */
  readonly data = input<unknown>(null);

  /**
   * When set to a non-null string, activates floating window mode and uses
   * the value as the window title. Leave null for inline mode.
   */
  readonly title = input<string | null>(null);

  /** Emitted when the user clicks the close button (floating mode only). */
  readonly closed = output<void>();

  // ── Floating window position ──────────────────────────────────────────────

  readonly x = signal(typeof window !== 'undefined' ? Math.round(window.innerWidth  * 0.2) : 120);
  readonly y = signal(typeof window !== 'undefined' ? Math.round(window.innerHeight * 0.1) : 60);

  // ── Keyboard ──────────────────────────────────────────────────────────────

  private readonly onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.title() !== null && this.data() !== null && this.data() !== undefined) {
      this.closed.emit();
    }
  };

  // ── Drag state ────────────────────────────────────────────────────────────

  private isDragging     = false;
  private pointerOffsetX = 0;
  private pointerOffsetY = 0;

  private readonly onMouseMove = (e: MouseEvent) => {
    if (!this.isDragging) return;
    this.x.set(e.clientX - this.pointerOffsetX);
    this.y.set(e.clientY - this.pointerOffsetY);
  };

  private readonly onMouseUp = () => {
    this.isDragging = false;
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup',   this.onMouseUp);
  };

  onDragStart(e: MouseEvent): void {
    if (e.button !== 0 || (e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    this.isDragging    = true;
    this.pointerOffsetX = e.clientX - this.x();
    this.pointerOffsetY = e.clientY - this.y();
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup',   this.onMouseUp);
  }

  // ── jsoneditor ────────────────────────────────────────────────────────────

  private readonly containerRef = viewChild<ElementRef<HTMLDivElement>>('container');
  private editor: JSONEditor | null = null;
  private _editorEl: HTMLDivElement | null = null;

  constructor() {
    document.addEventListener('keydown', this.onKeyDown);

    // afterEveryRender runs after each CD cycle.
    // We track the container element so that when the @if/@else branch switches
    // (e.g. title goes from null → non-null), we destroy the old editor and
    // create a fresh one on the new element. The cheap pointer comparison means
    // the no-op path costs almost nothing on subsequent renders.
    afterEveryRender(() => {
      const el = this.containerRef()?.nativeElement ?? null;
      if (el === this._editorEl) return;

      this.editor?.destroy();
      this.editor = null;
      this._editorEl = el;

      if (!el) return;
      this.editor = new JSONEditor(el, {
        mode: 'view',         // read-only tree; user can still expand/collapse and search
        mainMenuBar: true,    // toolbar with mode switcher and search
        navigationBar: true,  // breadcrumb path bar
        statusBar: false,     // hide bottom bar (line/column counts are irrelevant here)
        sortObjectKeys: true, // keep keys alphabetically sorted in the view
      });
      this.editor.set(toJson(this.data()));
      this.editor.expandAll();
    });

    // Re-render whenever the data input changes (no-op until the editor is created).
    effect(() => {
      const d = this.data();
      if (!this.editor) return;
      this.editor.set(toJson(d));
      this.editor.expandAll();
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown',   this.onKeyDown);
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup',   this.onMouseUp);
    this.editor?.destroy();
    this.editor = null;
  }
}
